<template>
  <div v-if="producer.role" class="position-relative px-3 pt-0 pb-3">
    <div @click="closeModal()" v-if="closeButton">
      <font-awesome-icon icon="times" class="close" />
    </div>
    <CRow
      v-if="withHeader"
      class="
        flex-row
        justify-content-start
        align-items-center
        bg-white
        producer-header
        bg-gray-500
        py-4
        px-2
        position-relative
      "
      :style="{ backgroundImage: 'url(' + backgroundImage + ')' }"
    >
      <div
        class="
          d-flex
          flex-column
          align-items-center
          p-3
          px-5
          producer-header__content
          position-absolute
        "
      >
        <figure
          v-if="producerLogo"
          class="d-flex justify-content-center align-items-center mb-2"
        >
          <img :src="producerLogo" alt="" class="" width="125" height="125" />
        </figure>

        <div class="d-flex flex-column align-items-center mb-3">
          <span class="d-inline-block ui-type-display-xl text-center">
            {{ producer.role.name }}
          </span>
          <span
            class="d-inline-block ui-type-heading mt-3 text-center"
            v-if="producerAddress"
          >
            {{ producerAddress.city }}
          </span>
        </div>
      </div>
    </CRow>

    <CRow class="my-3 p-0 px-3">
      <CCol sm="12" md="5" class="m-0">
        <div v-if="producerLogo && !withHeader" class="ui-type-display-md">
          <figure
            v-if="producerLogo"
            class="d-flex justify-content-start align-items-start"
          >
            <img :src="producerLogo" alt="" class="" width="75" height="75" />
          </figure>
        </div>
        <div v-if="producer" class="mt-3 ui-type-display-md">
          {{ producer.role.name }}
        </div>
        <div class="d-flex flex-column align-items-start mt-1 mb-2">
          <span
            class="d-inline-block ui-type-caption mr-3"
            v-if="producerAddress"
          >
            {{ producerAddress.to }}, {{ producerAddress.address }},
            {{ producerAddress.postcode }}
            {{ producerAddress.city }}
          </span>
        </div>

        <!-- PRODUCER DESCRIPTION -->
        <div
          v-if="producer.role.desc_short"
          class="mt-3 pb-2 ui-type-body"
          style="font-weight: bold"
          v-html="producer.role.desc_short"
        ></div>

        <!-- PRODUCER ZONE -->
        <CRow
          class="justify-content-end align-items-start flex-column mx-0"
          v-if="producer.role.desc_zone"
        >
          <div class="mt-1 mb-0 ui-type-heading w-100">
            {{ $t("models.role.desc_zone") }}:
          </div>

          <div class="mt-1 ui-type-body" v-html="producer.role.desc_zone"></div>
        </CRow>

        <CRow class="mb-3 mx-0">
          <div
            v-if="
              geocodedAddresses &&
              geocodedAddresses.lat &&
              geocodedAddresses.lng
            "
            class="overflow-hidden w-100 rounded mt-3"
          >
            <LMap
              ref="map"
              :zoom="10"
              :options="{
                zoomControl: true,
                scrollWheelZoom: false,
                doubleClickZoom: true,
                touchZoom: true,
                dragging: true,
              }"
              :center="[geocodedAddresses.lat, geocodedAddresses.lng]"
              style="height: 200px; width: 100%"
            >
              <LTileLayer
                :url="cartoTileUrl"
                attribution='&copy; <a href="https://stamen.com">Stamen Design</a>, &copy; <a href="https://openstreetmap.org">OpenStreetMap</a>'
              />
              <LMarker
                v-if="geocodedAddresses"
                :lat-lng="[geocodedAddresses.lat, geocodedAddresses.lng]"
                :icon="customMarkerIcon"
              >
              </LMarker>
              <LMarker
                v-if="geocodedMarketAddresses"
                :lat-lng="[
                  geocodedMarketAddresses.lat,
                  geocodedMarketAddresses.lng,
                ]"
                :icon="customMarkerIconMarket"
              >
              </LMarker>
              <!-- Green wavy lines from producers to family -->
              <LPolyline
                v-if="geocodedAddresses && geocodedMarketAddresses"
                :key="
                  'line-' + geocodedAddresses.lat + '-' + geocodedAddresses.lng
                "
                :lat-lngs="
                  generateWavyLine(
                    geocodedAddresses.lat,
                    geocodedAddresses.lng,
                    geocodedMarketAddresses.lat,
                    geocodedMarketAddresses.lng
                  )
                "
                :color="'#90b44c'"
                :weight="2"
                :opacity="1"
              />
            </LMap>
            <small>{{
              $t("models.role.profile-map-explanation", [
                producer.role.name,
                market.role.name,
              ])
            }}</small>
          </div>
          <div v-else-if="geocodingLoading" class="overflow-hidden w-100">
            <SkeletonCell width="100%" height="150px" />
          </div>
        </CRow>

        <!-- PRODUCER TRANSPARENCY -->
        <MarketProducerProfileTransparency
          :transparency="producer.role.extra.transparency"
        />
      </CCol>
      <CCol sm="12" md="7" class="m-0">
        <div v-if="hasSlideshow">
          <CCol sm="12" class="mx-0 px-0 mt-3">
            <flickity
              ref="mainFlickity"
              :options="flickityProduct"
              class="flickity-gallery mb-2"
            >
              <figure
                class="flickity-figure"
                v-for="(image, index) in slideshowImages"
                :key="`c_${image.id}`"
              >
                <img class="flickity-img" :src="image.image_md" alt="" />

                <div class="flickity-zoom">
                  <CButton
                    color="primary"
                    @click.prevent="onImageClick(image, index)"
                  >
                    <font-awesome-icon icon="search" />
                  </CButton>
                </div>
              </figure>
            </flickity>
          </CCol>
        </div>

        <CRow class="bg-gray-100 mt-3 p-3 mx-0 px-0 rounded w-100">
          <div class="ui-type-display-sm w-100 m-0">
            {{ $t("producer.products.title") }}:
          </div>

          <CRow v-if="!loading" class="mx-0 my-3 w-100">
            <CCol
              v-for="product in products"
              :key="product.id"
              sm="6"
              md="6"
              lg="4"
              xl="4"
              class="mb-3 px-2"
              @click="onCardClick(product.id)"
            >
              <MarketProductCard v-bind="product" />
            </CCol>
            <CCol sm="12" v-if="showLoadMore" class="text-left mt-3">
              <CButton
                color="primary"
                @click="loadMoreProducts"
                :disabled="loadingMore"
              >
                <span v-if="!loadingMore">{{
                  $t("common.general.loadMore")
                }}</span>
                <span v-else>{{ $t("common.general.loading") }}</span>
              </CButton>
            </CCol>
          </CRow>
          <SkeletonCell width="100%" height="150px" v-else />
        </CRow>
        <CRow class="bg-gray-100 mt-3 py-2 mx-0 px-0 rounded" v-if="hasGallery">
          <div class="ui-type-display-sm w-100 pl-3 mb-1 mt-1">
            {{ $t("role.profile.tab_images.filter_type.gallery") }}:
          </div>
          <CRow class="px-4">
            <CCol
              sm="6"
              md="4"
              lg="3"
              v-for="(image, index) in galleryImages"
              :key="index"
            >
              <MarketProducerProfileGalleryImage :image="image" />
            </CCol>
          </CRow>
        </CRow>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import get from "lodash/get";
import { geocodeAddress } from "../../../helpers/common";
import { LMap, LTileLayer, LMarker, LIcon, LPolyline } from "vue2-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import SkeletonCell from "../../../common/table/Skeleton.vue";
import MarketProductCard from "./MarketProductCard.vue";
import { apiGetRQL } from "../../../helpers/apiHelper";
import { getPriceUnitOptions } from "../../../helpers/options";
import { getProductCardDataFromMarketProduct } from "../../../helpers/common";
import { mapGetters } from "vuex";
import { apiGet } from "../../../helpers/apiHelper";
import { generateWavyLine, fitMapToMarkers } from "../../../helpers/common";
import { getCartoTileUrl } from "../../../config/mapTiles";
import { Fancybox } from "@fancyapps/ui";
import MarketProducerProfileGalleryImage from "./MarketProducerProfileGalleryImage.vue";
import MarketProducerProfileTransparency from "./MarketProducerProfileTransparency.vue";

export default {
  name: "MarketProducerProfile",

  components: {
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPolyline,
    SkeletonCell,
    MarketProductCard,
    MarketProducerProfileGalleryImage,
    MarketProducerProfileTransparency,
  },

  props: {
    producer: {
      type: Object,
      required: true,
    },
    market: {
      type: Object,
      required: true,
    },
    closeButton: {
      type: Boolean,
      required: false,
      default: true,
    },
    withHeader: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  data() {
    return {
      queryFilters: {
        limit: {
          style: "query",
          operator: "=",
          term: 6,
        },
        offset: {
          style: "query",
          operator: "=",
          term: 0,
        },
      },
      pagination: {
        count: 0,
        limit: 8,
        offset: 0,
      },
      prods: [],
      loading: true,
      imagesLoading: true,
      loadingMore: false,
      geocodedAddresses: null,
      geocodingLoading: false,
      customMarkerIcon: L.icon({
        iconUrl: "/loonity/img/locationpin_producer.png",
        iconSize: [50, 50],
      }),
      customMarkerIconMarket: L.icon({
        iconUrl: "/loonity/img/locationpin_market.png",
        iconSize: [50, 50],
      }),
      producerAddress: null,
      marketAddress: null,
      geocodedMarketAddresses: null,
      images: null,
      galleryImages: null,
      slideshowImages: null,
      backgroundImages: null,
      flickityProduct: {
        pageDots: false,
        prevNextButtons: true,
        imagesLoaded: true,
        autoPlay: 5000,
        pauseAutoPlayOnHover: true,
        wrapAround: true,
      },
    };
  },

  computed: {
    cartoTileUrl() {
      return getCartoTileUrl();
    },
    showLoadMore() {
      return this.pagination.count > this.prods.length;
    },

    producerLogo() {
      return get(this.producer, "role.logo_square.image_md", false);
    },

    ...mapGetters("connections", ["currencySymbol"]),

    products() {
      const lang = this.$i18n; //.locale;
      const currencySymbol = this.currencySymbol;
      const unitOptions = getPriceUnitOptions(this.$i18n);

      return this.prods.map((product) => {
        return getProductCardDataFromMarketProduct(
          product,
          lang,
          currencySymbol,
          unitOptions
        );
      });
    },

    backgroundImage() {
      if (this.imagesLoading) {
        return "";
      }
      return this.backgroundImages && this.backgroundImages.length > 0
        ? this.backgroundImages[0].image_xl
        : "/loonity/img/placeholder-bg.jpg";
    },

    hasSlideshow() {
      return this.slideshowImages && this.slideshowImages.length > 0;
    },

    hasGallery() {
      return this.galleryImages && this.galleryImages.length > 0;
    },
  },

  methods: {
    generateWavyLine,
    async fetchProducts(page = null) {
      this.loading = true;

      let filter = { ...this.queryFilters };

      if (page) {
        filter.offset = {
          style: "query",
          operator: "=",
          term: (page - 1) * this.queryFilters.limit.term,
        };
      }

      filter.producer = {
        style: "query",
        operator: "=",
        term: get(this.producer, "id", null),
      };

      let params = {
        endpoint: "family_market_products",
        params: [
          this.$store.state.connections.current.family.id,
          this.market.id,
        ],
        filter: filter,
      };

      const response = await apiGetRQL(params);

      if (response && response.data) {
        this.prods = response.data.payload.Items;
        this.pagination = {
          count: response.data.payload.Count,
          limit: parseInt(filter.limit.term),
          offset: parseInt(filter.offset.term),
        };
        this.loading = false;
      }
    },

    async loadMoreProducts() {
      if (this.loadingMore) return;
      this.loadingMore = true;
      // Calculate next offset
      const nextOffset = this.prods.length;
      let filter = { ...this.queryFilters };
      filter.offset = {
        style: "query",
        operator: "=",
        term: nextOffset,
      };
      filter.producer = {
        style: "query",
        operator: "=",
        term: get(this.producer, "id", null),
      };
      let params = {
        endpoint: "family_market_products",
        params: [
          this.$store.state.connections.current.family.id,
          this.market.id,
        ],
        filter: filter,
      };
      const response = await apiGetRQL(params);
      if (response && response.data) {
        this.prods = this.prods.concat(response.data.payload.Items);
        this.pagination = {
          count: response.data.payload.Count,
          limit: parseInt(filter.limit.term),
          offset: nextOffset,
        };
      }
      this.loadingMore = false;
    },

    isEmpty(obj) {
      return Object.keys(obj).length === 0 && obj.constructor === Object;
    },

    async getProducerAddress() {
      this.producerAddress = get(
        this.producer,
        "role.family.addresses[0]",
        null
      );

      const response = await apiGet(
        "geo_postalcode",
        [],
        this.producerAddress.postal_code
      );
      if (response && response.status === 200) {
        this.producerAddress.postcode = response.data.postal_code;
      }
    },

    async getMarketAddress() {
      this.marketAddress = get(this.market, "role.family.addresses[0]", null);

      const response = await apiGet(
        "geo_postalcode",
        [],
        this.marketAddress.postal_code
      );
      if (response && response.status === 200) {
        this.marketAddress.postcode = response.data.postal_code;
      }
    },

    async geocodeProducerAddress() {
      const addressData = this.producerAddress;
      if (addressData) {
        if (addressData.latitude && addressData.longitude) {
          this.geocodedAddresses = {
            lat: addressData.latitude,
            lng: addressData.longitude,
          };
          this.$nextTick(() =>
            fitMapToMarkers(
              this.$refs.map,
              this.geocodedAddresses,
              this.geocodedMarketAddresses
            )
          );
          return;
        }

        this.geocodingLoading = true;
        const geocodedAddress = await geocodeAddress(addressData);
        this.geocodingLoading = false;

        if (geocodedAddress && geocodedAddress.lat && geocodedAddress.lng) {
          this.geocodedAddresses = geocodedAddress;
          this.$nextTick(() =>
            fitMapToMarkers(
              this.$refs.map,
              this.geocodedAddresses,
              this.geocodedMarketAddresses
            )
          );
        } else {
          this.geocodedAddresses = null;
        }
      } else {
        this.geocodingLoading = false;
        this.geocodedAddresses = null;
      }
    },

    async geocodeMarketAddress() {
      const addressData = this.marketAddress;

      if (addressData) {
        if (addressData.latitude && addressData.longitude) {
          this.geocodedMarketAddresses = {
            lat: addressData.latitude,
            lng: addressData.longitude,
          };
          this.$nextTick(() =>
            fitMapToMarkers(
              this.$refs.map,
              this.geocodedAddresses,
              this.geocodedMarketAddresses
            )
          );
          return;
        }

        this.geocodingLoading = true;
        const geocodedAddress = await geocodeAddress(addressData);
        this.geocodingLoading = false;

        if (geocodedAddress && geocodedAddress.lat && geocodedAddress.lng) {
          this.geocodedMarketAddresses = geocodedAddress;
          this.$nextTick(() =>
            fitMapToMarkers(
              this.$refs.map,
              this.geocodedAddresses,
              this.geocodedMarketAddresses
            )
          );
        } else {
          this.geocodedMarketAddresses = null;
        }
      } else {
        this.geocodingLoading = false;
        this.geocodedMarketAddresses = null;
      }
    },

    async fetchProducerImages() {
      const producerId = this.producer.role.id;
      this.images = null;
      this.galleryImages = null;
      this.slideshowImages = null;
      this.backgroundImages = null;

      const response = apiGet("role_imagecrop", [producerId]);
      response
        .then((res) => {
          this.images = res.data.payload.Items.filter(
            (item) =>
              item.image.gallery === true ||
              item.image.slideshow === true ||
              item.image.background === true
          );
          this.galleryImages = res.data.payload.Items.filter(
            (item) => item.image.gallery === true
          );
          this.slideshowImages = res.data.payload.Items.filter(
            (item) => item.image.slideshow === true
          );
          this.backgroundImages = res.data.payload.Items.filter(
            (item) => item.image.background === true
          );
          this.imagesLoading = false;
        })
        .catch((res) => {
          console.error("Failed to fetch images:", res);
          this.imagesLoading = false;
        });
    },

    closeModal() {
      this.geocodedAddresses = {};
      this.$emit("close");
    },

    onCardClick(productId) {
      const familyId = this.$route.params.familyId;
      const marketId = this.market.id;
      this.$router.push({
        name: "UserMarketProduct",
        params: { familyId, marketId, productId },
      });
    },

    onImageClick(image, index) {
      const slideshow = this.slideshowImages.map((img) => {
        return { src: img.image.img, type: "image" };
      });
      const instance = new Fancybox(slideshow);
      instance.jumpTo(index);
    },
  },

  watch: {
    producer: {
      async handler() {
        await this.fetchProducerImages();
        await this.getProducerAddress();
        this.geocodeProducerAddress();
        if (!this.isEmpty(this.producer)) {
          this.fetchProducts();
        }
      },
      immediate: true,
      deep: true,
    },
    market: {
      async handler() {
        await this.getMarketAddress();
        this.geocodeMarketAddress();
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>
